


















































































import { Component } from "vue-property-decorator";
import Vue from "vue";
import { placeModule } from "@/store/modules/place";
import {
  getCommunityList,
  updateCommunityList,
  getFilteredIid,
  updateFilteredIid
} from "@/api/communitySettingsService";

type CommunityItemStatus = "saved" | "new" | "deleted";

interface CommunityItem {
  code: string;
  branch_name: string;
  status: CommunityItemStatus;
}

@Component
export default class CommunitySettings extends Vue {
  public communityItems: CommunityItem[] = [];
  public selectedCode: string | null = null;
  public communityLoading = false;
  public communitySaving = false;

  public filteredIidOriginal: string[] = [];
  public iidPasteText = "";
  public iidDraftText = "";
  public iidDialog = false;
  public iidLoading = false;
  public iidSaving = false;

  get places() {
    return placeModule.place;
  }

  get activeCount() {
    return this.communityItems.filter(item => item.status !== "deleted").length;
  }

  get placeOptions() {
    const usedCodes = new Set(
      this.communityItems.filter(item => item.status !== "deleted").map(item => item.code)
    );
    return this.places
      .filter(place => !usedCodes.has(`${place.shortcode}-${place.index}`))
      .slice()
      .sort((a, b) => {
        if (a.display !== b.display) return a.display ? -1 : 1;
        return `${a.shortcode}-${a.index}`.localeCompare(`${b.shortcode}-${b.index}`);
      })
      .map(place => ({
        code: `${place.shortcode}-${place.index}`,
        text: `${place.shortcode}-${place.index}（${place.branch_name}）`,
        branch_name: place.branch_name
      }));
  }

  get parsedPasteList() {
    const seen = new Set<string>();
    const result: string[] = [];
    this.iidPasteText
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .forEach(iid => {
        if (!seen.has(iid)) {
          seen.add(iid);
          result.push(iid);
        }
      });
    return result;
  }

  get iidDiff() {
    const pastedSet = new Set(this.parsedPasteList);
    const originalSet = new Set(this.filteredIidOriginal);
    const deleted = this.filteredIidOriginal
      .filter(iid => !pastedSet.has(iid))
      .map(iid => ({ iid, status: "deleted" as const }));
    const added = this.parsedPasteList
      .filter(iid => !originalSet.has(iid))
      .map(iid => ({ iid, status: "new" as const }));
    const existing = this.parsedPasteList
      .filter(iid => originalSet.has(iid))
      .map(iid => ({ iid, status: "existing" as const }));
    return [...deleted, ...added, ...existing];
  }

  iidStatusColor(status: "existing" | "new" | "deleted") {
    if (status === "deleted") return "red";
    if (status === "new") return "green";
    return "blue";
  }

  async created() {
    this.communityLoading = true;
    if (!this.places.length) {
      await placeModule.placeList(true);
    }
    const codes: string[] = await getCommunityList();
    this.communityItems = codes.map(code => {
      const place = this.places.find(p => `${p.shortcode}-${p.index}` === code);
      return { code, branch_name: place ? place.branch_name : code, status: "saved" as CommunityItemStatus };
    });
    this.communityLoading = false;

    this.iidLoading = true;
    this.filteredIidOriginal = await getFilteredIid();
    this.iidPasteText = this.filteredIidOriginal.join("\n");
    this.iidLoading = false;
  }

  onSelectPlace(code: string | null) {
    if (!code) return;
    const existing = this.communityItems.find(item => item.code === code);
    if (existing) {
      existing.status = "saved";
    } else {
      const place = this.places.find(p => `${p.shortcode}-${p.index}` === code);
      this.communityItems.push({
        code,
        branch_name: place ? place.branch_name : code,
        status: "new"
      });
    }
    this.$nextTick(() => {
      this.selectedCode = null;
    });
  }

  toggleDelete(item: CommunityItem) {
    if (item.status === "new") {
      this.communityItems = this.communityItems.filter(i => i !== item);
    } else if (item.status === "saved") {
      item.status = "deleted";
    } else {
      item.status = "saved";
    }
  }

  statusColor(status: CommunityItemStatus) {
    if (status === "new") return "green";
    if (status === "deleted") return "red";
    return "blue";
  }

  async saveCommunityList() {
    this.communitySaving = true;
    const finalCodes = this.communityItems.filter(item => item.status !== "deleted").map(item => item.code);
    const result = await updateCommunityList(finalCodes);
    if (result) {
      this.communityItems = this.communityItems
        .filter(item => item.status !== "deleted")
        .map(item => ({ ...item, status: "saved" as CommunityItemStatus }));
    }
    this.communitySaving = false;
  }

  openIidDialog() {
    this.iidDraftText = this.iidPasteText;
    this.iidDialog = true;
  }

  cancelIidDialog() {
    this.iidDialog = false;
  }

  applyIidDraft() {
    this.iidPasteText = this.iidDraftText;
    this.iidDialog = false;
  }

  async saveFilteredIid() {
    this.iidSaving = true;
    const result = await updateFilteredIid(this.parsedPasteList);
    if (result) {
      this.filteredIidOriginal = result;
      this.iidPasteText = result.join("\n");
    }
    this.iidSaving = false;
  }
}
